import { Button } from '@augmatica/ui'
import { type FormEvent, useId, useState } from 'react'

type Status = { state: 'idle' | 'submitting' | 'success' } | { state: 'error'; message: string }

interface WaitlistResponse {
  ok?: boolean
  error?: string
}

export function WaitlistForm() {
  const emailId = useId()
  const statusId = useId()
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [status, setStatus] = useState<Status>({ state: 'idle' })

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (status.state === 'submitting') return

    setStatus({ state: 'submitting' })

    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // `company` is the honeypot — a real person never fills it in.
        body: JSON.stringify({ email, company, source: 'landing' }),
      })
      const payload = (await response.json().catch(() => null)) as WaitlistResponse | null

      if (!response.ok) {
        setStatus({ state: 'error', message: payload?.error ?? 'Something went wrong. Please try again.' })
        return
      }

      setStatus({ state: 'success' })
    } catch {
      setStatus({ state: 'error', message: 'We could not reach the server. Check your connection and try again.' })
    }
  }

  if (status.state === 'success') {
    return (
      <p className="waitlist__done" role="status">
        <strong>You're on the list.</strong> We'll email {email} when Augmatica Studio opens up.
      </p>
    )
  }

  const submitting = status.state === 'submitting'

  return (
    <form className="waitlist" onSubmit={submit} aria-busy={submitting}>
      <label className="waitlist__label" htmlFor={emailId}>
        Email address
      </label>

      <div className="waitlist__row">
        <input
          id={emailId}
          className="waitlist__input"
          type="email"
          name="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="you@company.com"
          autoComplete="email"
          required
          disabled={submitting}
          aria-describedby={status.state === 'error' ? statusId : undefined}
          aria-invalid={status.state === 'error'}
        />
        <Button type="submit" size="lg" disabled={submitting}>
          {submitting ? 'Signing up…' : 'Sign me up'}
        </Button>
      </div>

      <div className="waitlist__honeypot" aria-hidden="true">
        <label htmlFor="company">Company</label>
        <input
          id="company"
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={company}
          onChange={(event) => setCompany(event.target.value)}
        />
      </div>

      <p className="waitlist__note" id={statusId} role={status.state === 'error' ? 'alert' : undefined}>
        {status.state === 'error' ? (
          <span className="waitlist__error">{status.message}</span>
        ) : (
          'One email when we launch. No newsletter, no sharing.'
        )}
      </p>
    </form>
  )
}
